/* (c) Dalineage, s.r.o. 2020-2025, all rights reserved */
package com.dalineage.client

import org.scalajs.dom
import org.scalajs.dom.document
import dom.html
import scala.scalajs.js

import com.dalineage.common

object Workspace {
  import common.adt.ExplorerDataADT._
  import common.adt.ADT
  import Console.msgBox
  import batchtree.TreeExplorer
  import adt.TreeExplorerADT
  import adt.PropertyPanelADT
  import TreeExplorerOps._

  import scala.concurrent.Future
  import scala.concurrent.ExecutionContext.Implicits.global

  import adt.PropertyPanelADT.{Button, KeyValue, HelpText, Property}
  import PropertyPanel.button

  var mainDiv: html.Div = scala.compiletime.uninitialized

  val location = dom.window.location
  val domain = s"${location.protocol}//${location.hostname}:${location.port}"

  trait WorkspaceUserAction
  case class KeyboardEvent(event: dom.KeyboardEvent) extends WorkspaceUserAction

  def init(
    mainDiv: html.Div,
    queryParams: Map[String, String]): Unit = {

    this.mainDiv = mainDiv

    val panels = Panels.init(mainDiv)
    val helpDiv: html.Div = Panels.helpDiv

    val userHelp = s"Logged in as <b>${js.Dynamic.global.user}</b>"
    val upload = Button("upload code", 85, "helpDiv")
    val uploadHelp = s"Press u to ${button(upload)}."
    val login = Button("login/logout", 76, "helpDiv")
    val loginHelp = s"Press l to ${button(login)}."
    val console = Button("view log console", 71, "helpDiv")
    val consoleHelp = s"Press g to ${button(console)}."
    val oButton = Button("o", 79, "helpDiv")
    val pButton = Button("p", 80, "helpDiv")
    val bButton = Button("b", 66, "helpDiv")
    val cButton = Button("c", 67, "helpDiv")

    val buttons = s"${button(oButton)} ${button(pButton)} ${button(bButton)} ${button(cButton)}"
    val panelsHelp = s"Press $buttons to shrink opening/property/object browser/code panels."
    val etwas = "@"
    val sp = "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
    Panels.helpDiv.innerHTML = s"$userHelp $loginHelp $sp $panelsHelp $sp $uploadHelp $consoleHelp"

    val workspaceUserActionFn: WorkspaceUserAction => Unit = action => action match
      case this.KeyboardEvent(event) if event.keyCode == 76 && !event.ctrlKey => // l
        loginLogout()
      case this.KeyboardEvent(event) if event.keyCode == 71 && !event.ctrlKey => // g
        Console.open()
      case this.KeyboardEvent(event) if event.keyCode == 85 && !event.ctrlKey => // u
        UploadDialog.open()
      case this.KeyboardEvent(event) if event.keyCode == 79 =>
        Panels.shrinkPanelToMinimum(0)
      case this.KeyboardEvent(event) if event.keyCode == 80 =>
        Panels.shrinkPanelToMinimum(1)
      case this.KeyboardEvent(event) if event.keyCode == 66 =>
        Panels.shrinkPanelToMinimum(2)
      case this.KeyboardEvent(event) if event.keyCode == 67 =>
        Panels.shrinkPanelToMinimum(3)
      case this.KeyboardEvent(event) =>
        println(s"Workspace Keyboard event: ${event.key}")
        mainDiv.dispatchEvent(event)
      case unhandled @ _ =>
        println(s"Unhandled action: $unhandled")

    helpDiv.addEventListener("keydown", { (ev: dom.KeyboardEvent) =>
      workspaceUserActionFn(this.KeyboardEvent(ev))
    })

    val explorerUserActionFn: TreeExplorerADT.TreeExplorerUserAction => Unit = { action =>
      action match
        case TreeExplorerADT.SwitchFocus() => panels(2).focus()
        case TreeExplorerADT.DeleteBatch(node, dir) =>
          msgBox(s"Deleting batch: ${node.key}")
          val user = node.properties.head match //user is kept as first property!
            case adt.PropertyPanelADT.KeyValue("user", user, _) => user
            case _ => throw new Exception(s"assertion failed")
          deleteBatch(user, node.key, dir)
        case TreeExplorerADT.NodeSelected(node) =>
          msgBox(s"Node selected: ${node.key}")
          PropertyPanel.setProperties(node.properties)
        case TreeExplorerADT.NodeOpened(node) =>
          msgBox(s"Batch opened: ${node.key}")
          val user = node.properties.head match //user is kept as first property!
            case adt.PropertyPanelADT.KeyValue("user", user, _) => user
            case _ => throw new Exception(s"assertion failed")
          val batchId = node.key
          val url =  s"?user=$user&id=$batchId"
          dom.window.location.search = url
        case TreeExplorerADT.DownloadOriginal(node) =>
          val user = node.properties.head.match
            case PropertyPanelADT.KeyValue("user", user, _) => user
            case _ => throw new Exception(s"assertion failed")
          val batchId = node.key
          val file = s"/web/download/orig/$user/$batchId"
          msgBox(s"Downloading file ${file}")
          downloadFile(file)
        case TreeExplorerADT.ExplorerKeyboardEvent(ev)  =>
          workspaceUserActionFn(this.KeyboardEvent(ev))
        case _ =>
          msgBox("Unhandled action! " + action)
    }

    batchtree.BatchTreeComponent.init(panels(0), explorerUserActionFn)
    batchtree.BatchTreeComponent.msg("Loading user data.")
    loadExplorerData({ data =>
      batchtree.BatchTreeComponent.open(data, queryParams.get("id"), queryParams.get("dir"))
    })

    queryParams.get("user").flatMap(user => queryParams.get("id").map(id => user -> id))
      .map{ case (user, id) =>

        val updateHelpText: String => Unit = println

        import diagram.GoJSDiagram.DiagramUserAction
        import common.adt.PropertyPanelADT._
        import adt.PropertyPanelADT._
        import PropertyPanel._
        import scala.concurrent.Future
        import scala.concurrent.ExecutionContext.Implicits.global
        import org.scalajs.dom.experimental.*
        import scala.scalajs.js
        val diagramUserActionFn: DiagramUserAction => Unit = { action =>
          action match
            case diagram.GoJSDiagram.LoadJson(url, fn) =>
              DomOps.getJson( url, fn )

            case diagram.GoJSDiagram.DiagramNodeSelected(optData) =>
              optData.map{ data =>
                val properties = diagram.DiagramContextMenu.menu(data)
                PropertyPanel.setProperties(properties)
              }

            case diagram.GoJSDiagram.ColumnSelected(optData) =>
              optData.map{ dataWithPanel =>
                val data = dataWithPanel._1
                val key = data("key").toString
                val tablekey = data("tablekey").toString
                val typ = "Projection column"
                val name = data("name").toString
                val properties =
                  KeyValue( PropertyName.ObjectType.toString, typ, None ) ::
                  KeyValue( PropertyName.ObjectName.toString, name, None ) ::
                  KeyValue( PropertyName.TableKey.toString, tablekey, None ) ::
                  KeyValue( PropertyName.ColumnKey.toString, key, None ) :: Nil
                PropertyPanel.setProperties( properties )
              }

            case diagram.GoJSDiagram.DiagramLinkSelected(Some(link)) =>
              import typings.gojs.{mod => go}
              println(s"DiagramLinkSelected")
              val fromKey = link.fromNode.key.toString
              val toKey = link.toNode.key.toString
              val fromPort = link.fromPort match
                case p:go.Panel => s"${p.portId}"
                case _ => "?"
              val toPort = link.toPort match
                case p:go.Panel => s"${p.portId}"
                case _ => "?"
              val properties =
                KeyValue( PropertyName.ObjectName.toString, "Link", None ) ::
                KeyValue( PropertyName.FromNode.toString, fromKey, None ) ::
                KeyValue( PropertyName.ToNode.toString, toKey, None ) ::
                KeyValue( PropertyName.FromPort.toString, fromPort, None ) ::
                KeyValue( PropertyName.ToPort.toString, toPort, None ) :: Nil
              PropertyPanel.setProperties( properties )

            case diagram.GoJSDiagram.KeyboardEvent(event)  =>
              workspaceUserActionFn(this.KeyboardEvent(event))
            case _ =>
        }

        val optSourceId = queryParams.get("source").flatMap{
          sourcepath =>
            val str = sourcepath.split("/").toList.last
            """(\d+)$""".r.findFirstIn(str).map(_.toInt)
        }

        val optSourceKey = queryParams.get("key")

        import com.dalineage.client.treecomponent
        import treecomponent.TreeExplorer
        import com.dalineage.common.adt.TreeNodeADT.TreeNode
        import treecomponent.TreeViewUserAction
        import io.circe._, io.circe.parser._, io.circe.syntax._
        panels(2).id = "tree-container"
        import upickle.default._

        implicit val positionRW: ReadWriter[ADT.Position] = macroRW
        implicit val rangeRW: ReadWriter[ADT.Range] = macroRW

        import treecomponent.TreeExplorer.fullPath
        import util.chaining._
        val objectBrowserUserActionFn: objectbrowser.ObjectBrowserUserAction => Future[Unit] =
          action => action match
            case objectbrowser.ShowDiagram(node) =>
              Future {
                node.properties.get(PropertyName.CodePosition.id) match
                  case Some(position) if node.`type` == ObjectType.Constant.id =>
                    println(s"no open action for constant")
                  case Some(position) =>
                    val range = read[ADT.Range](position)
                    s"?user=$user&id=$id&source=" pipe { url =>
                      node.`type` match
                        case objType if objType == ObjectType.Query.id =>
                          java.net.URLEncoder.encode(node.key, "UTF-8") pipe {
                            encodedKey =>
                              s"$url${fullPath(node).drop(1).init.mkString("/")}&key=${encodedKey}"
                          }
                        case _ =>
                          s"$url${fullPath(node).drop(1).mkString("/")}"
                    } pipe { url =>
                      //println(s"url ${url}")
                      dom.window.location.search = url
                    }
                  case None =>
                    println(s"Code position not found for node ${node.key}")
              }
            case objectbrowser.ShowCode(node) =>
              Future {
                node.properties.get(PropertyName.CodePosition.id) match
                  case Some(position) =>
                    val range = read[ADT.Range](position)
                    Editor.selectCode(
                      range.sourceId.toString,
                      range.from.line, range.from.column,
                      range.to.line, range.to.column)
                    Console.msgBox(s"Code opened, sourceId ${range.sourceId}")
                  case None =>
                    println(s"Code position not found for node ${node.key}")
              }

            case objectbrowser.SwitchFocus() =>
              Future{ panels(4).focus() } //i don't know why this focuses panels(0)

            case objectbrowser.KeyboardEvent(ev)  =>
              Future{
                workspaceUserActionFn(this.KeyboardEvent(ev))
              }
            case _ => 
              Future{ println(s"action ${action}") }

        val initialKey = queryParams.get("source") match
          case Some(source) => Some("BATCH" :: "SOURCES" :: source.split("/").toList)
          case None => None
        objectbrowser.ObjectBrowser.init(panels(2), user, id, objectBrowserUserActionFn, initialKey)

        println(s"initialKey " + pprint.apply(initialKey))

        diagram.GoJSDiagram
          .open(panels(4), user, id, optSourceId.map(_.toString), optSourceKey, diagramUserActionFn)
        Editor.init(panels(3))
        optSourceId.map(_.toString).fold
          ( Editor.open(user, id, "0") )
          ( Editor.open(user, id, _))
      }.getOrElse{
        panels(2).innerHTML = """
        <div class="obtext">&nbsp;</div>
        <ul>
        <li>
        <div class="obtext">Use arrow keys or mouse on the left
        panel to select batch</div>
        </li>
        <li>
        <div class="obtext">Press enter to load visualization diagram
        and see object browser data here</div>
        </li>
        <li>
        <div class="obtext">Register at info@dalineage.com
        to upload your own code</div>
        </li>
        </ul>
"""
        panels(4).innerHTML = """
        <div class="feedback">&nbsp;</div>
        <div class="feedback">Send feedback</div>
        <div class="feedback">Report bug</div>
        <div class="feedback">Request account:</div>
        <div class="feedback">&nbsp;</div>
        <div class="feedback">info@dalineage.com</div>
"""
      }

    PropertyPanel.init(panels(1))
  }

  def loadExplorerData(successFn: ExplorerData => Unit): Unit = {
    val dFn: ExplorerData => Unit = { data =>
      msgBox("User directory data loaded")
      successFn(data)
    }
    msgBox("Loading user directory data")
    DomOps.getJsonObject[ExplorerData]( "web/dashboard", dFn )
  }

  def loginLogout(): Unit =
    if (s"${js.Dynamic.global.user}" == "public")
      msgBox(s"Redirecting to login page")
      dom.window.location.replace("/login")
    else
      DomOps.sendRequest(dom.HttpMethod.GET, "web/logout", { text =>
        msgBox(s"Logged out, response: $text")
        DomOps.sendRequest(
          dom.HttpMethod.GET,
          "web/current_user",
          { user =>
            js.Dynamic.global.user = user
            msgBox(s"Logged out, current user is now $user")
            dom.document.location.reload()
          },
          noCache = true
        )
      })

  def downloadFile(filePath: String): Unit = {
    val aa = js.Dynamic.global.a
    aa.download = filePath.substring(filePath.lastIndexOf("/") + 1);
    val a = aa.asInstanceOf[dom.html.Anchor]
    a.href = filePath
    dom.document.body.appendChild(a)
    a.click()
    dom.document.body.removeChild(a)
  }

  def deleteBatch(user: String, batchId: String, dir: String): Unit = {
    val path = s"web/batch/$user/$batchId"
    msgBox(s"Sending delete batch request, batchId $batchId")
    DomOps.sendRequest(dom.HttpMethod.DELETE, path, {
      _ => dom.window.location.replace(s"/?dir=$dir")
    })
  }
}
